import { get, set, del } from "idb-keyval";
import {
  startAuthentication,
  WebAuthnError,
  startRegistration,
} from "@simplewebauthn/browser";
function generateChallenge() {
  const random = crypto.getRandomValues(new Uint8Array(32));
  let binary = "";
  for (let i = 0; i < random.length; i++) {
    binary += String.fromCharCode(random[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}
export function generateUUID() {
  // Insecure fallback (not cryptographically safe)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx"?.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
export function generateSecureUUID() {
  // Secure fallback using crypto.getRandomValues
  const arr = new Uint8Array(16);
  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(arr);
  } else {
    return generateUUID(); // fallback to insecure
  }

  arr[6] = (arr[6] & 0x0f) | 0x40;
  arr[8] = (arr[8] & 0x3f) | 0x80;

  return [...arr]
    .map((b, i) =>
      [4, 6, 8, 10].includes(i)
        ? "-" + b.toString(16).padStart(2, "0")
        : b.toString(16).padStart(2, "0")
    )
    .join("");
}
export function safeUUID() {
  if (typeof crypto?.randomUUID === "function") {
    return crypto.randomUUID(); // ✅ modern API
  } else if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    return generateSecureUUID(); // ✅ secure fallback
  } else {
    return generateUUID(); // ⚠️ insecure fallback
  }
}
export function generateCredentialId() {
  return safeUUID();
}
export async function tryStartAuthentication(credentialId) {
  try {
    const payload = {
      // challenge: "setup-" + safeUUID(),
      challenge: generateChallenge(),
      rpID: window.location.hostname,
      userVerification: "required",
      allowCredentials: [
        {
          id: credentialId,
          type: "public-key",
          transports: ["internal", "usb", "nfc", "ble", "hybrid"],
        },
      ],
    };
    const result = await startAuthentication({ optionsJSON: payload });

    return result; // ✅ Success
  } catch (err) {
    console.error("Biometric authentication failed:", err);
    return null; // ❌ Failed
  }
}

export async function exportPublicKeyToPem(publicKey) {
  const spki = await crypto.subtle.exportKey("spki", publicKey);
  const b64 = btoa(String.fromCharCode(...new Uint8Array(spki)));
  return `-----BEGIN PUBLIC KEY-----${b64}-----END PUBLIC KEY-----`;
}

export async function encryptString(input, password) {
  const enc = new TextEncoder();
  const pwKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const key = await crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: 100000, hash: "SHA-256" },
    pwKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"]
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode(input)
  );
  const payload = new Uint8Array([
    ...salt,
    ...iv,
    ...new Uint8Array(encrypted),
  ]);
  return btoa(String.fromCharCode(...payload));
}

export async function saveCredentialForUser(
  username,
  credentialId,
  privateKey,
  encryptedUsername,
  encryptedCredentialId
) {
  await set(credentialId, {
    privateKey,
    encryptedUsername,
    encryptedCredentialId,
  });

  const existingList = (await get(username)) || [];
  if (!existingList.includes(credentialId)) {
    // existingList.push(credentialId);
    await set(username, credentialId);
  }
}

export async function getCredentialIdsForUser(username) {
  return (await get(username)) || [];
}

export async function tryStartRegistration(username) {
  try {
    const userIdBytes = new TextEncoder().encode(username);
    const userIdBase64url = bufferToBase64url(userIdBytes);

    const registrationOptions = {
      challenge: generateChallenge(),
      rp: {
        id: window.location.hostname,
        name: "Profile imediL",
      },
      user: {
        id: userIdBase64url,
        name: username,
        displayName: username,
      },

      pubKeyCredParams: [
        { type: "public-key", alg: -7 },
        { type: "public-key", alg: -257 },
      ], // ES256
      authenticatorSelection: {
        authenticatorAttachment: "platform",
        userVerification: "required",
      },
      timeout: 60000,
      attestation: "none",
    };

    const attestationResponse = await startRegistration({
      optionsJSON: registrationOptions,
    });

    const credentialIdFromWebAuthn = attestationResponse?.id;

    // Optionally store credentialIdFromWebAuthn somewhere (indexedDB, server, etc)

    return { credentialIdFromWebAuthn };
  } catch (err) {
    if (err instanceof WebAuthnError) {
      // See `name`, `message`, `code`, and `cause` for more info
    } else {
      // Something else went wrong
    }
    console.error("WebAuthn registration failed:", err);
    return null;
  }
}
export async function biometricSetup(username) {
  const registrationResult = await tryStartRegistration(username);
  if (registrationResult) {
    const { credentialIdFromWebAuthn } = registrationResult;
    const { publicKey, privateKey } = await crypto.subtle.generateKey(
      {
        name: "RSASSA-PKCS1-v1_5",
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: "SHA-256",
      },
      true,
      ["sign", "verify"]
    );

    const publicKeyPem = await exportPublicKeyToPem(publicKey);
    // const credentialId = generateCredentialId();
    const credentialId = credentialIdFromWebAuthn || generateCredentialId(); // use WebAuthn ID if present
    const encryptedUsername = await encryptString(username, credentialId);
    const encryptedCredentialId = await encryptString(credentialId, username);

    await saveCredentialForUser(
      username,
      credentialId,
      privateKey,
      encryptedUsername,
      encryptedCredentialId
    );

    return { publicKeyPem, credentialId };
  } else {
    return;
  }
}

export async function biometricLoginSign(credentialId, payload) {
  const authResult = await tryStartAuthentication(credentialId);

  if (authResult) {
    const stored = await get(credentialId);
    if (!stored || !stored.privateKey)
      throw new Error("Biometric credential not found");

    const payloadBuffer = new TextEncoder().encode(payload);

    const signatureBuffer = await crypto.subtle.sign(
      { name: "RSASSA-PKCS1-v1_5" },
      stored.privateKey,
      payloadBuffer
    );

    const bytes = new Uint8Array(signatureBuffer);

    return btoa(String.fromCharCode(...bytes));
  } else {
    return;
  }
}
const DEVICE_GUID_USER_MAP = "user-device-guid-map";

/**
 * Only retrieves the device GUID for a given user, if it exists
 * @param {string} personalId
 * @returns {Promise<string|null>}
 */
export async function getDeviceGuidForUser(personalId) {
  const map = (await get(DEVICE_GUID_USER_MAP)) || {};
  return map[personalId] || null;
}

/**
 * Creates a new GUID for the user only if one doesn't exist.
 * Will not overwrite an existing value.
 * @param {string} personalId
 * @returns {Promise<string>} newly created or existing GUID
 */
export async function createDeviceGuidIfNeeded(personalId) {
  const map = (await get(DEVICE_GUID_USER_MAP)) || {};

  if (map[personalId]) {
    return map[personalId]; // already exists, return it
  }

  const newGuid = safeUUID();
  map[personalId] = newGuid;

  await set(DEVICE_GUID_USER_MAP, map);
  return newGuid;
}

/**
 * Deletes biometric credential:
 * 1. Uses username to find the credentialId
 * 2. Deletes the credentialId entry
 * 3. Deletes the username-to-credentialId mapping
 *
 * @param {string} username
 */
export async function deleteBiometricCredentialByUsername(username) {
  const credentialId = await get(username);

  if (!credentialId) {
    console.warn(`No credentialId found for user ${username}`);
    return;
  }

  // Step 1: Delete the actual credential object
  await del(credentialId);

  // Step 2: Delete the mapping from username
  await del(username);
}
